import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Mail, Smartphone, MessageCircle, TrendingUp, BarChart3 } from 'lucide-react';
import LanguageSwitcher from '../components/shared/LanguageSwitcher';
import { usePageTracking } from '../hooks/usePageTracking';
import { trackCtaClick } from '../utils/googleAnalytics';

const Kampanja = () => {
  const navigate = useNavigate();
  usePageTracking();

  const marketingChannels = [
    {
      name: 'SMS Kampanje',
      icon: <Smartphone className="h-12 w-12 text-blue-600" />,
      description: 'Direktno slanje SMS poruka vašim klijentima za podsetnike, obaveštenja i promocije.',
      features: [
        'Automatski podsetnici za zakazane termine',
        'Obaveštenja o statusu servisa',
        'Promotivne poruke i akcije',
        'Visoka stopa otvaranja (99%)',
        'Trenutna dostava poruka',
        'Personalizovane poruke'
      ],
      color: 'bg-blue-50',
      iconColor: 'text-blue-600'
    },
    {
      name: 'Viber Kampanje',
      icon: <MessageCircle className="h-12 w-12 text-purple-600" />,
      description: 'Iskorišćite Viber za direktnu komunikaciju sa klijentima kroz obogaćene poruke sa slikama i linkovima.',
      features: [
        'Rič tekst poruke sa formatiranjem',
        'Slanje slika i dokumenata',
        'Interaktivni dugmići i linkovi',
        'Viber Business poruke',
        'Automatski odgovori',
        'Praćenje statusa poruka'
      ],
      color: 'bg-purple-50',
      iconColor: 'text-purple-600'
    },
    {
      name: 'WhatsApp Kampanje',
      icon: <MessageSquare className="h-12 w-12 text-green-600" />,
      description: 'Komunikacija sa klijentima preko WhatsApp-a, najpopularnijeg messaging servisa u svetu.',
      features: [
        'WhatsApp Business API integracija',
        'Slanje tekstualnih i multimedijalnih poruka',
        'Status poruke (poslato/primljeno/pročitano)',
        'Automatski odgovori i chatbot',
        'Grupne poruke za masovne kampanje',
        'Visoka angažovanost korisnika'
      ],
      color: 'bg-green-50',
      iconColor: 'text-green-600'
    },
    {
      name: 'Email Kampanje',
      icon: <Mail className="h-12 w-12 text-red-600" />,
      description: 'Profesionalne email kampanje sa detaljnim izveštajima i analitikom otvaranja.',
      features: [
        'Prilagođeni email template-i',
        'Automatski podsetnici i obaveštenja',
        'Detaljna analitika (otvoreno, kliknuto)',
        'Segmentacija korisnika',
        'A/B testiranje poruka',
        'Integracija sa kalendarom'
      ],
      color: 'bg-red-50',
      iconColor: 'text-red-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center text-gray-700 hover:text-gray-900"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Nazad
              </button>
              <span className="text-2xl font-bold text-gray-900">SpinTasker</span>
            </div>
            <LanguageSwitcher />
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-purple-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Marketing Kampanje
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              Komunikujte sa svojim klijentima na najefikasniji način kroz SMS, Viber, WhatsApp i Email kampanje
            </p>
          </div>
        </div>
      </section>

      {/* Marketing Channels */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {marketingChannels.map((channel, index) => (
              <div
                key={index}
                className={`${channel.color} rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all`}
              >
                <div className="flex items-start mb-6">
                  <div className={`${channel.iconColor} mr-4 flex-shrink-0`}>
                    {channel.icon}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      {channel.name}
                    </h3>
                    <p className="text-gray-700 text-lg mb-6">
                      {channel.description}
                    </p>
                  </div>
                </div>
                
                <ul className="space-y-3">
                  {channel.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Zašto koristiti marketing kampanje?
            </h2>
            <p className="text-xl text-gray-600">
              Povećajte angažovanost klijenata i smanjite broj neispunjenih termina
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-blue-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="h-10 w-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Automatski podsetnici
              </h3>
              <p className="text-gray-600">
                Smanjite broj neispunjenih termina sa automatskim podsetnicima pre zakazanog servisa
              </p>
            </div>

            <div className="text-center">
              <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-10 w-10 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Povećana angažovanost
              </h3>
              <p className="text-gray-600">
                Direktna komunikacija sa klijentima kroz njihove omiljene kanale povećava odziv
              </p>
            </div>

            <div className="text-center">
              <div className="bg-purple-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="h-10 w-10 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Detaljna analitika
              </h3>
              <p className="text-gray-600">
                Pratite uspešnost kampanja i optimizujte komunikaciju na osnovu rezultata
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Spremni da počnete?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Registrujte se danas i počnite da koristite marketing kampanje za vaš servis
          </p>
          <button
            onClick={() => {
              trackCtaClick('register', { cta_location: 'kampanja_cta' });
              navigate('/auth?role=company&type=register');
            }}
            className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-colors"
          >
            Započni besplatno
          </button>
        </div>
      </section>
    </div>
  );
};

export default Kampanja;
